export type EmployeeTimeSheetColumnKey =
  | 'employee'
  | 'hireDate'
  | 'status'
  | 'daysInTraining'
  | 'workedHours'
  | 'absenceDays'
  | 'absenceHours'
  | 'tardyStart'
  | 'tardyBreak'
  | 'tardyLunch'
  | 'leavingEarly'
  | 'manualAdj'
  | 'verbalWarnings'
  | 'writtenWarnings'
  | 'finalWarnings'
  | 'totalWarnings';

export type EmployeeTimeSheetSortKey =
  | 'employee'
  | 'worked'
  | 'absenceDays'
  | 'absenceHours'
  | 'tardyStart'
  | 'tardyBreak'
  | 'tardyLunch'
  | 'leavingEarly'
  | 'daysInTraining';

export interface EmployeeTimeSheetColumnDef {
  key: EmployeeTimeSheetColumnKey;
  label: string;
  shortLabel: string;
  tooltip: string;
  required?: boolean;
  sortKey?: EmployeeTimeSheetSortKey;
  align?: 'left' | 'center' | 'right';
  widthPct: number;
  csvHeader: string;
  excelHeader: string;
  pdfHeader: string;
}

export const EMPLOYEE_TIME_SHEET_REPORT_KEY = 'employee-time-sheet';

export const EMPLOYEE_TIME_SHEET_COLUMNS: EmployeeTimeSheetColumnDef[] = [
  {
    key: 'employee',
    label: 'Employee',
    shortLabel: 'Employee',
    tooltip: 'Employee name and ID',
    required: true,
    sortKey: 'employee',
    widthPct: 16,
    csvHeader: 'Employee Name',
    excelHeader: 'Employee Name',
    pdfHeader: 'Employee Name',
  },
  {
    key: 'hireDate',
    label: 'Hire Date',
    shortLabel: 'Hired',
    tooltip: 'Hire date (bonus: hired on or before the 1st of the period month)',
    widthPct: 7,
    csvHeader: 'Hire Date',
    excelHeader: 'Hire Date',
    pdfHeader: 'Hire Date',
  },
  {
    key: 'status',
    label: 'Status',
    shortLabel: 'Status',
    tooltip: 'Assignment status and On Training mark for the selected period',
    widthPct: 10,
    csvHeader: 'Status',
    excelHeader: 'Status',
    pdfHeader: 'Status',
  },
  {
    key: 'daysInTraining',
    label: 'Days in Training',
    shortLabel: 'Train Days',
    tooltip: 'Distinct days scheduled on the Training campaign within the selected period',
    sortKey: 'daysInTraining',
    align: 'center',
    widthPct: 6,
    csvHeader: 'Days in Training',
    excelHeader: 'Days in Training',
    pdfHeader: 'Train Days',
  },
  {
    key: 'workedHours',
    label: 'Total Worked Hours',
    shortLabel: 'Worked',
    tooltip: 'Total worked hours',
    sortKey: 'worked',
    align: 'center',
    widthPct: 6,
    csvHeader: 'Total Worked Minutes',
    excelHeader: 'Total Worked Minutes',
    pdfHeader: 'Total Worked Hours',
  },
  {
    key: 'absenceDays',
    label: 'Absences (Days)',
    shortLabel: 'Abs Days',
    tooltip: 'Absence days',
    sortKey: 'absenceDays',
    align: 'center',
    widthPct: 6,
    csvHeader: 'Absences (Days)',
    excelHeader: 'Absences (Days)',
    pdfHeader: 'Absences (Days)',
  },
  {
    key: 'absenceHours',
    label: 'Absences (Hours)',
    shortLabel: 'Abs Hrs',
    tooltip: 'Absence hours',
    sortKey: 'absenceHours',
    align: 'center',
    widthPct: 6,
    csvHeader: 'Absences (Minutes)',
    excelHeader: 'Absences (Minutes)',
    pdfHeader: 'Absences (Hours)',
  },
  {
    key: 'tardyStart',
    label: 'Tardies (Start)',
    shortLabel: 'Tardy In',
    tooltip: 'Tardies at start',
    sortKey: 'tardyStart',
    align: 'center',
    widthPct: 7,
    csvHeader: 'Tardies (Start Minutes)',
    excelHeader: 'Tardies (Start Minutes)',
    pdfHeader: 'Tardies (Start)',
  },
  {
    key: 'tardyBreak',
    label: 'Tardies (Break)',
    shortLabel: 'Tardy Brk',
    tooltip: 'Tardies returning from break',
    sortKey: 'tardyBreak',
    align: 'center',
    widthPct: 7,
    csvHeader: 'Tardies (Break Minutes)',
    excelHeader: 'Tardies (Break Minutes)',
    pdfHeader: 'Tardies (Break)',
  },
  {
    key: 'tardyLunch',
    label: 'Tardies (Lunch)',
    shortLabel: 'Tardy Lnc',
    tooltip: 'Tardies returning from lunch',
    sortKey: 'tardyLunch',
    align: 'center',
    widthPct: 7,
    csvHeader: 'Tardies (Lunch Minutes)',
    excelHeader: 'Tardies (Lunch Minutes)',
    pdfHeader: 'Tardies (Lunch)',
  },
  {
    key: 'leavingEarly',
    label: 'Leaving Early',
    shortLabel: 'Left Early',
    tooltip: 'Leaving early',
    sortKey: 'leavingEarly',
    align: 'center',
    widthPct: 7,
    csvHeader: 'Leaving Early (Minutes)',
    excelHeader: 'Leaving Early (Minutes)',
    pdfHeader: 'Leaving Early',
  },
  {
    key: 'manualAdj',
    label: 'Manual Adj.',
    shortLabel: 'Manual Adj.',
    tooltip: 'Manual time record adjustment events in the report period',
    align: 'center',
    widthPct: 7,
    csvHeader: 'Manual Adjustments',
    excelHeader: 'Manual Adjustments',
    pdfHeader: 'Manual Adj.',
  },
  {
    key: 'verbalWarnings',
    label: 'Verbal',
    shortLabel: 'Verb',
    tooltip: 'Verbal warnings in date range',
    align: 'center',
    widthPct: 5,
    csvHeader: 'Verbal',
    excelHeader: 'Verbal',
    pdfHeader: 'Verbal',
  },
  {
    key: 'writtenWarnings',
    label: 'Written',
    shortLabel: 'Writ',
    tooltip: 'Written warnings in date range',
    align: 'center',
    widthPct: 5,
    csvHeader: 'Written',
    excelHeader: 'Written',
    pdfHeader: 'Written',
  },
  {
    key: 'finalWarnings',
    label: 'Final',
    shortLabel: 'Final',
    tooltip: 'Final warnings in date range',
    align: 'center',
    widthPct: 5,
    csvHeader: 'Final',
    excelHeader: 'Final',
    pdfHeader: 'Final',
  },
  {
    key: 'totalWarnings',
    label: 'Total Warnings',
    shortLabel: 'Warn',
    tooltip: 'Total warnings in date range',
    align: 'center',
    widthPct: 7,
    csvHeader: 'Total Warnings',
    excelHeader: 'Total Warnings',
    pdfHeader: 'Total Warnings',
  },
];

export const DEFAULT_EMPLOYEE_TIME_SHEET_COLUMNS: EmployeeTimeSheetColumnKey[] =
  EMPLOYEE_TIME_SHEET_COLUMNS.map((column) => column.key);

export function normalizeEmployeeTimeSheetColumns(
  columns?: string[] | null,
): EmployeeTimeSheetColumnKey[] {
  // Empty or legacy "employee only" selections mean "use all columns"
  if (
    !columns
    || columns.length === 0
    || (columns.length === 1 && columns[0] === 'employee')
  ) {
    return [...DEFAULT_EMPLOYEE_TIME_SHEET_COLUMNS];
  }

  const allowed = new Set(DEFAULT_EMPLOYEE_TIME_SHEET_COLUMNS);
  const selected = columns
    .filter((key): key is EmployeeTimeSheetColumnKey => allowed.has(key as EmployeeTimeSheetColumnKey));

  if (!selected.includes('employee')) {
    selected.unshift('employee');
  }

  // Upgrade saved prefs from before Train Days existed (previous full default was 15 keys).
  if (
    selected.includes('status')
    && !selected.includes('daysInTraining')
    && selected.length === DEFAULT_EMPLOYEE_TIME_SHEET_COLUMNS.length - 1
  ) {
    selected.splice(selected.indexOf('status') + 1, 0, 'daysInTraining');
  }

  return selected.length > 0 ? selected : [...DEFAULT_EMPLOYEE_TIME_SHEET_COLUMNS];
}

export function getEmployeeTimeSheetColumnDefs(
  columns?: string[] | null,
): EmployeeTimeSheetColumnDef[] {
  const visibleKeys = normalizeEmployeeTimeSheetColumns(columns);
  const defs = EMPLOYEE_TIME_SHEET_COLUMNS.filter((column) => visibleKeys.includes(column.key));
  const totalWeight = defs.reduce((sum, column) => sum + column.widthPct, 0) || 1;

  return defs.map((column) => ({
    ...column,
    widthPct: (column.widthPct / totalWeight) * 100,
  }));
}

export function getEmployeeTimeSheetColumnDef(
  key: EmployeeTimeSheetColumnKey,
): EmployeeTimeSheetColumnDef {
  return EMPLOYEE_TIME_SHEET_COLUMNS.find((column) => column.key === key)!;
}
